/**
 * Which Good through Date the Payoff page opens on, kept out of the page so it can be read and
 * tested on its own.
 */

/** The part of a logged payoff statement this rule reads. */
export interface QuotedStatement {
  payoffDate: string
  status: string
  createdAt: string
}

/**
 * The Good through Date this loan was last quoted for, or '' when it has never been quoted.
 *
 * Opening on today instead is what made a payoff look wrong the day after it was issued. Every line
 * on the Payoff page is accrued to the date in that box, so a demand sent good through the 13th
 * re-read a full per-diem higher when the servicer opened the page on the 14th to reconcile the
 * money that arrived against it: on longhorn 49338 that was 56c of force-placed insurance interest
 * at 18% and $75.80 of note interest, and it was reported as the force-placed insurance accrual
 * being wrong rather than as a quote for a different day. Opening on the date the borrower was
 * actually quoted makes the page reproduce the demand it is being checked against.
 *
 * An ISSUED statement wins over a draft: a draft is an internal quote the servicer may well have
 * thrown away, while an approved one is the demand somebody is paying against.
 *
 * Newest first within each, by the date the statement was CREATED and not by its Good through Date,
 * because the last statement the servicer produced is the one they are working from even when it is
 * quoted to an earlier day. Compared as strings, which is an ordering only because `createdAt` is
 * an ISO timestamp.
 *
 * Rows carrying no date are dropped rather than sorted: an empty Good through Date is not a quote,
 * and letting one win would open the page blank on a loan that has been quoted.
 */
export const lastQuotedDate = (statements: QuotedStatement[]): string => {
  const quoted = (statements || [])
    // A copy, so sorting cannot reorder the log the page renders from.
    .filter((row) => `${row.payoffDate || ''}`.trim())
    .sort((a, b) => `${b.createdAt || ''}`.localeCompare(`${a.createdAt || ''}`))
  const issued = quoted.find((row) => row.status === 'approved')
  return (issued || quoted[0])?.payoffDate || ''
}
